import { Form, Formik } from "formik";
import * as Yup from "yup";
import { App } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { Button, Input } from "../../../custom";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import { changePassword } from "../../../utils/auth_request";
import { userAtom } from "../../../utils/store";
import { validator } from "../../../utils/validator";
import { logout } from "../../../utils/logout";
import styles from "./styles.module.scss";

const validationSchema = Yup.object().shape({
  currentPassword: validator.password,
  newPassword: validator.password,
  confirmNewPassword: Yup.string()
    .required("Confirm new password is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const ChangePassword = () => {
  const { notification } = App.useApp();
  const storedUser = useAtomValue(userAtom);

  const changePasswordMutation = useMutation({
    mutationKey: ["change-password"],
    mutationFn: changePassword,
  });

  const handleSubmit = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => {
    const payload: ChangePasswordPayload = {
      email: storedUser?.email ?? "",
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      confirmNewPassword: values.confirmNewPassword,
    };

    try {
      await changePasswordMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message ?? "Password changed successfully. Logging you out...",
            onClose: logout,
          });
          setTimeout(logout, 2000);
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message ?? "Failed to change password.",
      });
    }
  };

  return (
    <PageLayout heading="Change Password">
      <div className={styles.container}>
        <p className={styles.subtitle}>
          Enter your current password and choose a new password.
        </p>

        <Formik
          initialValues={{ currentPassword: "", newPassword: "", confirmNewPassword: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form className={styles.form}>
              <Input
                name="currentPassword"
                type="password"
                label="Current Password:"
              />
              <Input
                name="newPassword"
                type="password"
                label="New Password:"
              />
              <Input
                name="confirmNewPassword"
                type="password"
                label="Confirm New Password:"
              />

              <div className={styles.btnWrapper}>
                <Button
                  text="CHANGE PASSWORD"
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  isLoading={changePasswordMutation.isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PageLayout>
  );
};

export default ChangePassword;
